import Text from 'components/Text/Text';
import Mcc from './components/Mcc/Mcc';
import React, { useCallback, useContext, useEffect } from 'react';
import { Form } from 'antd';
import { VivaWalletCredentialsSelect } from './components/VivaWalletCredentialsSelect';
import AdsField from './components/AdsField';
import MenuVersionField from './components/MenuVersionField';
import ContentVersionField from './components/ContentVersionField';
import { MixpanelTokenField } from './components/MixpanelTokenField';
import { StopListSyncIntervalField } from './components/StopListSyncIntervalField';
import { BtnBlock } from './components/BtnBlock';
import ClientTypeSelect from './components/ClientTypeSelect';
import BillTemplateBlock from 'components/BillTemplateBlock/BillTemplateBlock';
import PriceTypeField from './components/PriceTypeField';
import EmailBodyTemplateField from './components/EmailBodyTemplateField';
import EmailField from './components/EmailField';
import NameField from './components/NameField';
import { TipsBlock } from 'components/TipsBlock/TipsBlock';
import ShowLoyaltyProgramButtonsCheckBox from './components/ShowLoyaltyProgramButtonsCheckBox';
import Tags from './components/Tags/Tags';
import DefaultLanguageSelect from './components/DefaultLanguageSelect';
import LanguagesMultipleSelect from './components/LanguagesMultipleSelect';
import { ActivateBPACheckBox } from './components/ActivateBPACheckBox';
import { IMccCard, CardBrands } from 'types';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';
import { EditCustomerContext, IEditCustomerContext } from '../../../../../../editCustomerState/editCustomerContext';
import { getFileIdForSubmit, preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

export const InputFields: React.FC = () => {
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);

	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		changeName,
		changeEmail,
		changeEmailBodyTemplate,
		setClientType,
		changeBillTemplateFileData,
		changeBillAdvertisingImageData,
		changeBillAdvertisingLink,
		deleteBillAdvertisingImage,
		changePriceType,
		setTips0,
		setTips1,
		setTips2,
		setTips3,
		setDefaultTips,
		toggleShowLoyaltyProgramButtons,
		addMccCard,
		deleteMccCard,
		changeBaseMcc,
		changeMainMccCardBrand,
		changeMccCardBrand,
		changeMainMccCardMcc,
		changeMccCardMcc,
		setVivaWalletCredentialsId,
		setAds,
		changeMenuVersion,
		changeContentVersion,
		setMixpanelToken,
		setStopListSyncIntervalMinutes,
		setCustomerDefaultLanguage,
		addTags,
		changeTags,
		deleteTag,
		setCustomerLanguages,
		setFetchedLanguages,
		toggleActivateBPA,
		customerId,
		filePath,
		fileId,
		name,
		email,
		emailBodyTemplate,
		clientType,
		billTemplateFileData,
		billTemplateFilePath,
		billAdvertisingImageData,
		billAdvertisingImage,
		billAdvertisingLink,
		priceType,
		tips0,
		tips1,
		tips2,
		tips3,
		defaultTips,
		isShowLoyaltyProgramButtons,
		baseMcc,
		mainMccCardBrand,
		mainMccCardMcc,
		mccCards,
		vivaWalletCredentialsId,
		ads,
		menuVersion,
		contentVersion,
		mixpanelToken,
		stopListSyncIntervalMinutes,
		IDSelectedCustomerDefaultLanguage,
		IDsSelectedCustomerLanguages,
		tags,
		isActiveBpa,
	} = useContext<IEditCustomerContext>(EditCustomerContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			name,
			email,
			emailBodyTemplate,
			clientType,
			billAdvertisingLink,
			priceType,
			tips0,
			tips1,
			tips2,
			tips3,
			defaultTips,
			isShowLoyaltyProgramButtons,
			baseMcc,
			mainMccCardBrand,
			mainMccCardMcc,
			vivaWalletCredentialsId,
			ads,
			menuVersion,
			contentVersion,
			mixpanelToken,
			stopListSyncIntervalMinutes,
			IDSelectedCustomerDefaultLanguage,
			IDsSelectedCustomerLanguages,
			isActiveBpa,
		});
		mccCards.forEach((el) => {
			form.setFieldsValue({ [`cardBrand-${el.id}`]: el.brand, [`mcc-${el.id}`]: el.mcc });
		});
	}, [
		name,
		email,
		emailBodyTemplate,
		clientType,
		billAdvertisingLink,
		priceType,
		tips0,
		tips1,
		tips2,
		tips3,
		defaultTips,
		isShowLoyaltyProgramButtons,
		baseMcc,
		mainMccCardBrand,
		mainMccCardMcc,
		mccCards,
		vivaWalletCredentialsId,
		ads,
		menuVersion,
		contentVersion,
		mixpanelToken,
		stopListSyncIntervalMinutes,
		IDSelectedCustomerDefaultLanguage,
		IDsSelectedCustomerLanguages,
		isActiveBpa,
	]);

	const reservedMccCardBrands: (CardBrands | null)[] = [mainMccCardBrand, ...mccCards.map((el) => el.brand)];

	const submit = async () => {
		setMainFormValidationFail(false);

		const finalMccCards = () => {
			const result: Pick<IMccCard, 'brand' | 'mcc'>[] = [];

			if (mainMccCardBrand && mainMccCardMcc) {
				result.push({ brand: mainMccCardBrand, mcc: mainMccCardMcc });
			}

			mccCards.forEach((el) => {
				if (el.brand && el.mcc) {
					result.push({ brand: el.brand, mcc: el.mcc });
				}
			});

			return result;
		};

		setDataForSubmit({
			id: customerId,
			name,
			fileId: getFileIdForSubmit({ fileId, filePath }),
			email,
			emailBodyTemplate,
			clientType: clientType ? clientType : 1,
			billTemplateId: getFileIdForSubmit({
				fileId: billTemplateFileData.fileId,
				filePath: billTemplateFilePath,
			}),
			billAdvertisingImage: getFileIdForSubmit({
				fileId: billAdvertisingImageData.fileId,
				filePath: billAdvertisingImage,
			}),
			billAdvertisingLink,
			priceType,
			defaultTips: [
				{ value: tips0, isDefault: defaultTips === '0' },
				{ value: tips1, isDefault: defaultTips === '1' },
				{ value: tips2, isDefault: defaultTips === '2' },
				{ value: tips3, isDefault: defaultTips === '3' },
			],
			isShowLoyaltyProgramButtons,
			vivaWalletCredentialsId,
			ads: Number(ads),
			menuVersion: menuVersion ? menuVersion : null,
			contentVersion: contentVersion ? contentVersion : null,
			mixpanelToken: mixpanelToken || null,
			stopListSyncIntervalMinutes: stopListSyncIntervalMinutes || 0,
			defaultLanguageId: IDSelectedCustomerDefaultLanguage || '',
			availableLanguageIds: IDsSelectedCustomerLanguages,
			tags: tags,
			merchantCategoryCode: {
				baseMcc: baseMcc || 0,
				mccCards: finalMccCards(),
			},
			isActiveBpa,
		});
		additionalValidationErrors.length
			? setMainFormValidationFail(true)
			: changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleFormValidationFail = useCallback(() => setMainFormValidationFail(true), []);

	const handleImageFileSizeExceeded = useCallback(() => changeTypeOfModalWindow('imageFileSizeExceeded'), []);
	const handleHtmlFileSizeExceeded = useCallback(() => changeTypeOfModalWindow('htmlFileSizeExceeded'), []);

	return (
		<div className={style.container} data-test="InputFields">
			<Form
				form={form}
				name="basic"
				onFinish={submit}
				onKeyPress={preventDefaultForEnterKey}
				onFinishFailed={handleFormValidationFail}
				layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<NameField changeName={changeName} />
						<EmailField changeEmail={changeEmail} />
					</div>
					<div className={style.row}>
						<EmailBodyTemplateField changeEmailBodyTemplate={changeEmailBodyTemplate} />
						<ClientTypeSelect setClientType={setClientType} />
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<BillTemplateBlock
						changeBillTemplateFileData={changeBillTemplateFileData}
						changeBillAdvertisingImageData={changeBillAdvertisingImageData}
						changeBillAdvertisingLink={changeBillAdvertisingLink}
						onImageFileSizeExceeded={handleImageFileSizeExceeded}
						onHtmlFileSizeExceeded={handleHtmlFileSizeExceeded}
						onDeleteBillAdvertisingImage={deleteBillAdvertisingImage}
						billTemplateFilePath={billTemplateFilePath}
						billAdvertisingImageData={billAdvertisingImageData}
						billAdvertisingImage={billAdvertisingImage}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="priceTypeHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<PriceTypeField changePriceType={changePriceType} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="tipsHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<TipsBlock
						setTips0={setTips0}
						setTips1={setTips1}
						setTips2={setTips2}
						setTips3={setTips3}
						setDefaultTips={setDefaultTips}
						defaultTips={defaultTips}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="loyaltyProgramHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<ShowLoyaltyProgramButtonsCheckBox
						toggleShowLoyaltyProgramButtons={toggleShowLoyaltyProgramButtons}
						isShowLoyaltyProgramButtons={isShowLoyaltyProgramButtons}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mccHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<Mcc
						changeBaseMcc={changeBaseMcc}
						changeMccCardMcc={changeMccCardMcc}
						changeMainMccCardMcc={changeMainMccCardMcc}
						changeMainMccCardBrand={changeMainMccCardBrand}
						changeMccCardBrand={changeMccCardBrand}
						addMccCard={addMccCard}
						deleteMccCard={deleteMccCard}
						selectedMainMccCardBrand={mainMccCardBrand}
						mccCards={mccCards}
						reservedMccCardBrands={reservedMccCardBrands}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Tags
						addTags={addTags}
						changeTags={changeTags}
						deleteTag={deleteTag}
						tags={tags}
						IDSelectedCustomerDefaultLanguage={IDSelectedCustomerDefaultLanguage}
						IDsSelectedCustomerLanguages={IDsSelectedCustomerLanguages}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="credentialsNameHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<VivaWalletCredentialsSelect setVivaWalletCredentialsId={setVivaWalletCredentialsId} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="adsHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<AdsField setAds={setAds} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="menuContentVersionHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<MenuVersionField changeMenuVersion={changeMenuVersion} />
						<ContentVersionField changeContentVersion={changeContentVersion} />
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="languagesHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.row}>
						<DefaultLanguageSelect
							setCustomerDefaultLanguage={setCustomerDefaultLanguage}
							setFetchedLanguages={setFetchedLanguages}
						/>
						<LanguagesMultipleSelect
							IDsSelectedCustomerLanguages={IDsSelectedCustomerLanguages}
							IDSelectedCustomerDefaultLanguage={IDSelectedCustomerDefaultLanguage}
							setCustomerLanguages={setCustomerLanguages}
						/>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="enableStopListHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<StopListSyncIntervalField setStopListSyncIntervalMinutes={setStopListSyncIntervalMinutes} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="bpaHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<Text
						translationNameSpace="form"
						translationKey="BPAIsUsedOnlyByThoseUsersWhoHaveEnteredIntoAgreementToWorkUnderBPAScheme"
						size="xs"
						theme="neutralLight"
					/>
					<ActivateBPACheckBox toggleActivateBPA={toggleActivateBPA} isActiveBpa={isActiveBpa} />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mixpanelHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<MixpanelTokenField setMixpanelToken={setMixpanelToken} />
				</div>
				<BtnBlock />
			</Form>
		</div>
	);
};
